import React from 'react';
import { Truck, Award, Leaf, Heart } from 'lucide-react';

const About = () => {
  return (
    <div className="bg-white">
      {/* Hero Section */}
      <section className="relative h-[500px]">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1591073113125-e46713c829ed?auto=format&fit=crop&q=80"
            alt="Mango Farm"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 to-black/50"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
          <div className="text-white max-w-2xl">
            <h1 className="text-4xl md:text-6xl font-bold mb-6">Our Legacy</h1>
            <p className="text-xl leading-relaxed">
              A tale of passion, tradition, and the pursuit of mango perfection spanning three generations
            </p>
          </div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-20 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">A Journey of Flavor and Tradition</h2>
            <div className="prose prose-lg text-gray-600 space-y-6">
              <p className="text-lg leading-relaxed">
                In the heart of Andhra Pradesh, where the sun-kissed orchards stretch as far as the eye can see, 
                our story began three generations ago. Our grandfather started with a modest dream and a small 
                plot of land, nurturing each mango tree with the wisdom passed down through generations of 
                Indian farmers.
              </p>
              <p className="text-lg leading-relaxed">
                Today, we proudly continue this legacy across our expansive orchards, where traditional 
                farming wisdom meets modern sustainable practices. Each mango variety we grow—from the 
                luxurious Alphonso to the sweet Banganapalli—tells a story of patient cultivation and 
                unwavering dedication to quality.
              </p>
              <p className="text-lg leading-relaxed">
                What sets us apart isn't just our mangoes—it's our commitment to preserving the authentic 
                taste that many remember from their childhood. We believe in letting nature take its course, 
                allowing each mango to ripen naturally on the tree, developing the perfect balance of 
                sweetness and aroma that artificial ripening simply cannot replicate.
              </p>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-20">
            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Leaf className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Natural Farming</h3>
              <p className="text-gray-600">
                Embracing traditional methods that respect both nature and tradition
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Heritage Quality</h3>
              <p className="text-gray-600">
                Maintaining the highest standards passed down through generations
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Family Values</h3>
              <p className="text-gray-600">
                Treating every customer as part of our extended family
              </p>
            </div>

            <div className="text-center p-6">
              <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="h-8 w-8 text-yellow-500" />
              </div>
              <h3 className="text-xl font-semibold mb-2">Direct to You</h3>
              <p className="text-gray-600">
                Bringing orchard-fresh mangoes straight to your doorstep
              </p>
            </div>
          </div>

          {/* Heritage Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <img
                src="https://images.unsplash.com/photo-1553279768-865429fa0078?auto=format&fit=crop&q=80"
                alt="Our Heritage"
                className="rounded-lg shadow-lg"
              />
            </div>
            <div className="space-y-6">
              <h2 className="text-3xl font-bold mb-6">Our Heritage</h2>
              <div className="prose prose-lg text-gray-600">
                <p>
                  Our orchards span over 100 acres of fertile land, carefully selected for its perfect 
                  combination of soil composition, climate, and elevation. Each location has been chosen 
                  to provide the ideal growing conditions for specific mango varieties, ensuring they 
                  develop their distinctive flavors and characteristics.
                </p>
                <p>
                  We take immense pride in our role as custodians of these ancient mango varieties. 
                  Our cultivation methods combine time-tested traditional practices with modern sustainable 
                  techniques, ensuring that future generations can continue to enjoy these magnificent 
                  fruits just as nature intended.
                </p>
                <p>
                  From the orchard to your table, every mango passes through multiple quality checks, 
                  ensuring that only the finest fruits bear our name. This commitment to excellence has 
                  earned us the trust of customers across India and beyond.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Commitment Section */}
      <section className="py-16 bg-yellow-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Our Commitment to Excellence</h2>
            <p className="text-gray-600 max-w-3xl mx-auto">
              Every mango that leaves our orchards carries with it our family's promise of quality, 
              authenticity, and the true taste of Indian summer.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Natural Ripening Process</h3>
              <p className="text-gray-600">
                We believe in nature's timing. Our mangoes are allowed to ripen naturally on the trees, 
                developing complex flavors and aromas that artificial ripening can never replicate. This 
                patience results in fruits that are not just delicious, but also retain their natural 
                nutritional value.
              </p>
            </div>
            <div className="bg-white p-6 rounded-lg shadow-md">
              <h3 className="text-xl font-semibold mb-4">Sustainable Practices</h3>
              <p className="text-gray-600">
                Our farming practices focus on environmental sustainability and biodiversity. We use organic 
                fertilizers and natural pest control methods, ensuring that our orchards remain healthy and 
                productive for generations to come.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;